// The read cache of the web app (Board #2594): a page opened again shows what it showed last time AT ONCE,
// then what is new. Every API read the app makes (the app's own client and every SDK's: they all call
// `fetch`) goes through it; nothing else changes for a page.
//
//   - For a moment after a navigation (`NAV_WINDOW_MS`, closed early by the person's first click or key),
//     a read the cache has an answer for is answered
//     from it at once, and asked again in the background; when the new answer differs, the cache takes
//     it and tells the shell (`onReadCacheUpdate`), which shows the page again with it. After the window
//     (a page polling, a reload button) every read goes to the network, and its answer is kept.
//   - In that window, an answer younger than `FRESH_MS` (a read just made, or prefetched when the pointer
//     reached the nav item) is answered as it is, with no second ask. Outside it a read always asks: a
//     Refresh button must show what another person, an agent or the api wrote meanwhile, which this
//     cache cannot see. Two identical reads in flight are one request.
//   - Any write (POST, PUT, PATCH, DELETE) to the API empties the cache: nothing written is ever shown as
//     it was before. So does a change of who is signed in.
//   - The reads a page made in its window are remembered per page, so pointing at its nav item reads
//     them ahead (`prefetchPage`).
// Only successful JSON answers of GET requests to the API are kept, never an event stream.

export const NAV_WINDOW_MS = 2_000;
export const FRESH_MS = 3_000;
export const MAX_AGE_MS = 10 * 60_000;
const MAX_ENTRIES = 300;
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_LEARNED = 16;
const LEARNED_KEY = 'cortex:read-cache:learned:v1';

interface Entry {
  body: string;
  status: number;
  headers: [string, string][];
  at: number;
}

export interface ReadCacheOptions {
  /** The API's origin and path prefix (apiBaseUrl). */
  apiBase: string;
  fetch?: typeof fetch;
  now?: () => number;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
}

export interface ReadCache {
  /** The fetch every caller uses once the cache is installed. */
  fetch: typeof fetch;
  /** A navigation to `page` (the route's page key, per project): opens the window, names the page. */
  navigated(page: string): void;
  /** The person acted on the page (a click, a key): the window closes, so a Refresh asks the api. */
  interacted(): void;
  /** Reads ahead what `page` read last time (nothing when it was never opened). */
  prefetchPage(page: string): void;
  /** Called when an answer shown from the cache in the current window changed. */
  onUpdate(listener: () => void): () => void;
  /** Forgets every answer (sign-out, another person). */
  clear(): void;
  /** For tests: the number of answers kept. */
  readonly size: number;
}

export function createReadCache(o: ReadCacheOptions): ReadCache {
  const base = o.fetch ?? globalThis.fetch.bind(globalThis);
  const now = o.now ?? (() => Date.now());
  const apiPrefix = `${o.apiBase.replace(/\/$/, '')}/api/`;
  const entries = new Map<string, Entry>();
  const inflight = new Map<string, Promise<Entry | Response>>();
  const listeners = new Set<() => void>();
  let windowStart = Number.NEGATIVE_INFINITY;
  let page: string | null = null;
  let revalidated = new Set<string>();
  let generation = 0;
  /** Who the cached answers are for (the bearer's subject), and the bearer to read ahead with. */
  let person: string | null | undefined;
  let authHeader: string | null = null;
  const learned = loadLearned(o.storage);

  const clear = () => {
    entries.clear();
    inflight.clear();
    generation++;
  };

  const inWindow = () => now() - windowStart < NAV_WINDOW_MS;

  const toResponse = (e: Entry) => new Response(e.body, { status: e.status, headers: e.headers });

  const keep = (url: string, e: Entry) => {
    entries.delete(url);
    entries.set(url, e);
    while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
  };

  const learn = (url: string) => {
    if (!page) return;
    const list = learned.get(page) ?? [];
    if (list.includes(url)) return;
    list.push(url);
    if (list.length > MAX_LEARNED) list.shift();
    learned.set(page, list);
    saveLearned(o.storage, learned);
  };

  /** One network read, kept when it can be; concurrent reads of the URL share it. */
  const load = (url: string, init: RequestInit): Promise<Entry | Response> => {
    const running = inflight.get(url);
    if (running) return running;
    const gen = generation;
    const { signal: _ignored, ...rest } = init;
    const p = (async (): Promise<Entry | Response> => {
      const res = await base(url, { ...rest, method: 'GET' });
      const type = res.headers.get('content-type') ?? '';
      if (res.status !== 200 || !type.includes('application/json')) return res;
      const body = await res.text();
      const e: Entry = { body, status: res.status, headers: [...res.headers.entries()], at: now() };
      // A write in the meantime: this answer may be from before it, so it is not kept.
      if (gen === generation && body.length <= MAX_BYTES) keep(url, e);
      return e;
    })();
    inflight.set(url, p);
    const done = () => inflight.get(url) === p && inflight.delete(url);
    p.then(done, done);
    return p;
  };

  const answer = async (p: Promise<Entry | Response>, signal: AbortSignal | null | undefined) => {
    const r = signal ? await abortable(p, signal) : await p;
    return r instanceof Response ? r.clone() : toResponse(r);
  };

  const cachedFetch: typeof fetch = async (input, init) => {
    // openapi-fetch (the app's client and every SDK's) calls fetch with a Request.
    const req = input instanceof Request ? input : null;
    const url = req ? req.url : String(input);
    const method = (init?.method ?? req?.method ?? 'GET').toUpperCase();
    if (!url.startsWith(apiPrefix) || method === 'HEAD') return base(input, init);
    if (method !== 'GET') {
      clear();
      try {
        return await base(input, init);
      } finally {
        clear();
      }
    }
    const headers = new Headers(init?.headers ?? req?.headers);
    if (
      (headers.get('accept') ?? '').includes('text/event-stream') ||
      (init?.cache ?? req?.cache) === 'no-store'
    )
      return base(input, init);
    // Another person (or none): nothing kept for the last one is theirs.
    const auth = headers.get('authorization');
    const who = subjectOf(auth);
    if (who !== person) {
      if (person !== undefined) clear();
      person = who;
    }
    authHeader = auth;
    const fetchInit: RequestInit = {
      headers,
      ...(req
        ? { credentials: req.credentials }
        : init?.credentials
          ? { credentials: init.credentials }
          : {}),
    };
    const signal = init?.signal ?? req?.signal ?? null;
    const hit = entries.get(url);
    const age = hit ? now() - hit.at : Number.POSITIVE_INFINITY;
    if (inWindow()) learn(url);
    if (hit && age < FRESH_MS && inWindow()) return toResponse(hit);
    if (hit && age < MAX_AGE_MS && inWindow()) {
      if (!revalidated.has(url)) {
        revalidated.add(url);
        const shownAt = windowStart;
        void load(url, fetchInit).then(
          (fresh) => {
            if (fresh instanceof Response || fresh.body === hit.body) return;
            if (shownAt === windowStart) for (const l of listeners) l();
          },
          () => undefined,
        );
      }
      return toResponse(hit);
    }
    return answer(load(url, fetchInit), signal);
  };

  return {
    fetch: cachedFetch,
    navigated(p) {
      page = p;
      windowStart = now();
      revalidated = new Set();
    },
    interacted() {
      windowStart = Number.NEGATIVE_INFINITY;
    },
    prefetchPage(p) {
      if (!authHeader) return;
      for (const url of learned.get(p) ?? []) {
        const hit = entries.get(url);
        if (hit && now() - hit.at < FRESH_MS) continue;
        void load(url, { headers: { authorization: authHeader } }).catch(() => undefined);
      }
    },
    onUpdate(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    clear,
    get size() {
      return entries.size;
    },
  };
}

/** The subject of a bearer JWT (not verified: it only keys the cache), else the header itself. */
function subjectOf(auth: string | null): string | null {
  if (!auth) return null;
  const payload = /^Bearer\s+[^.]+\.([^.]+)\./i.exec(auth)?.[1];
  if (!payload) return auth;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: unknown };
    return typeof json.sub === 'string' ? `sub:${json.sub}` : auth;
  } catch {
    return auth;
  }
}

function abortable<T>(p: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    p.then(
      (v) => {
        signal.removeEventListener('abort', onAbort);
        resolve(v);
      },
      (e) => {
        signal.removeEventListener('abort', onAbort);
        reject(e);
      },
    );
  });
}

function loadLearned(storage: ReadCacheOptions['storage']): Map<string, string[]> {
  try {
    const raw = storage?.getItem(LEARNED_KEY);
    const obj = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    return new Map(
      Object.entries(obj).filter(
        (e): e is [string, string[]] => Array.isArray(e[1]) && e[1].every((u) => typeof u === 'string'),
      ),
    );
  } catch {
    return new Map();
  }
}

function saveLearned(storage: ReadCacheOptions['storage'], learned: Map<string, string[]>) {
  try {
    // The most recent pages only: a long session never grows it without end.
    const recent = [...learned.entries()].slice(-60);
    storage?.setItem(LEARNED_KEY, JSON.stringify(Object.fromEntries(recent)));
  } catch {
    /* not persisted */
  }
}
